import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface EmojiItem {
  shortcode: string;
  url: string;
}

export interface EmojiPack {
  id: string;
  pubkey: string;
  dTag: string;
  title: string;
  description: string;
  image: string;
  emojis: EmojiItem[];
  createdAt: number;
  event: NostrEvent;
}

function parseEmojiPack(event: NostrEvent): EmojiPack | null {
  const dTag = event.tags.find(([name]) => name === 'd')?.[1];
  if (!dTag) return null;

  const title = event.tags.find(([name]) => name === 'title')?.[1] ?? dTag;
  const description = event.tags.find(([name]) => name === 'description')?.[1] ?? '';
  const image = event.tags.find(([name]) => name === 'image')?.[1] ?? '';
  const emojis: EmojiItem[] = event.tags
    .filter(([name]) => name === 'emoji')
    .map(([, shortcode, url]) => ({ shortcode, url }))
    .filter((e) => e.shortcode && e.url);

  return {
    id: event.id,
    pubkey: event.pubkey,
    dTag,
    title,
    description,
    image,
    emojis,
    createdAt: event.created_at,
    event,
  };
}

/** Fetch global emoji packs (kind 30030) */
export function useEmojiPacks(limit = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['emoji-packs', limit],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [30030], limit }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );

      return events
        .map(parseEmojiPack)
        .filter((p): p is EmojiPack => p !== null && p.emojis.length > 0);
    },
  });
}

/** Fetch emoji packs for a specific user */
export function useUserEmojiPacks(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['emoji-packs', 'user', pubkey],
    enabled: !!pubkey,
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [30030], authors: [pubkey!], limit: 100 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );

      return events
        .map(parseEmojiPack)
        .filter((p): p is EmojiPack => p !== null);
    },
  });
}

/** Fetch user's emoji list (kind 10030) — which packs they've added to their profile */
export function useUserEmojiList(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['emoji-list', pubkey],
    enabled: !!pubkey,
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [10030], authors: [pubkey!], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );

      const event = events[0];
      if (!event) return { event: null, packRefs: [] };

      // Extract "a" tags pointing to kind 30030 emoji sets
      const packRefs = event.tags
        .filter(([name, value]) => name === 'a' && value?.startsWith('30030:'))
        .map(([, value]) => value);

      return { event, packRefs };
    },
  });
}

/**
 * Fetch the exact packs referenced in a user's kind 10030 emoji list.
 * Resolves each `30030:<pubkey>:<dTag>` ref directly via relay filters,
 * so nothing is missed due to global query limits or authorship filtering.
 */
export function useSavedEmojiPacks(packRefs: string[]) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['emoji-packs', 'saved', packRefs],
    enabled: packRefs.length > 0,
    queryFn: async ({ signal }) => {
      // Parse refs into { pubkey, dTag } pairs
      const coords = packRefs
        .map((ref) => {
          const parts = ref.split(':');
          // format: "30030:<pubkey>:<dTag>"
          if (parts.length < 3 || parts[0] !== '30030') return null;
          const pubkey = parts[1];
          const dTag = parts.slice(2).join(':'); // dTag may itself contain colons
          if (!pubkey || !dTag) return null;
          return { pubkey, dTag };
        })
        .filter((c): c is { pubkey: string; dTag: string } => c !== null);

      if (coords.length === 0) return [];

      // Group by unique pubkeys so we can batch into as few relay queries as possible
      const byPubkey = new Map<string, string[]>();
      for (const { pubkey, dTag } of coords) {
        if (!byPubkey.has(pubkey)) byPubkey.set(pubkey, []);
        byPubkey.get(pubkey)!.push(dTag);
      }

      const filters = Array.from(byPubkey.entries()).map(([pubkey, dTags]) => ({
        kinds: [30030] as number[],
        authors: [pubkey],
        '#d': dTags,
        limit: dTags.length,
      }));

      const events = await nostr.query(
        filters,
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );

      const packs = events
        .map(parseEmojiPack)
        .filter((p): p is EmojiPack => p !== null);

      // Return in the same order as the original packRefs
      const packMap = new Map(packs.map((p) => [`30030:${p.pubkey}:${p.dTag}`, p]));
      return packRefs
        .map((ref) => packMap.get(ref))
        .filter((p): p is EmojiPack => p !== undefined);
    },
  });
}

/** Fetch a single emoji pack by naddr coordinates */
export function useEmojiPack(pubkey: string, dTag: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['emoji-pack', pubkey, dTag],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [30030], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]) },
      );

      if (!events[0]) return null;
      return parseEmojiPack(events[0]);
    },
  });
}

export { parseEmojiPack };
